import React, { useRef, useEffect, useState } from 'react';
import styles from '../../styles/home.module.css';

const TestimonialSection = ({
  subtitle,
  title,
  testimonials
}) => {
  const testimonialsRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth < 600) {
        setItemsPerPage(1);
      } else if (window.innerWidth < 960) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(3);
      }
    };

    updateItemsPerPage();
    window.addEventListener("resize", updateItemsPerPage);
    return () => window.removeEventListener("resize", updateItemsPerPage);
  }, []);

  const scrollToPage = (pageNumber) => {
    const container = testimonialsRef.current;
    if (!container || itemsPerPage === 0) return;

    const scrollAmount = (pageNumber - 1) * container.offsetWidth;
    container.scrollTo({
      left: scrollAmount,
      behavior: "smooth",
    });
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    const container = testimonialsRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isDragging) return;

      const scrollLeft = container.scrollLeft;
      const cardWidth = container.firstChild?.offsetWidth || 0;
      const gap = 20;
      const scrollPosition = scrollLeft + (cardWidth + gap) / 2;
      const newPage = Math.floor(scrollPosition / (cardWidth + gap)) + 1;

      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    };

    const handleMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
    };

    const handleMouseLeave = () => setIsDragging(false);
    const handleMouseUp = () => setIsDragging(false);

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
    };

    const handleTouchStart = (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
    };

    const handleTouchEnd = () => setIsDragging(false);

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const x = e.touches[0].pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
    };

    container.addEventListener("scroll", handleScroll);
    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mouseleave", handleMouseLeave);
    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("touchstart", handleTouchStart);
    container.addEventListener("touchend", handleTouchEnd);
    container.addEventListener("touchmove", handleTouchMove);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("mouseleave", handleMouseLeave);
      container.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchmove", handleTouchMove);
    };
  }, [currentPage, itemsPerPage, isDragging, startX, scrollLeft]);

  return (
    <section className={styles.testimonialSection}>
      <h4 className={styles.testimonialSubtitle}>{subtitle}</h4>
      <h2 className={styles.testimonialTitle}>{title}</h2>

      <div ref={testimonialsRef} className={styles.testimonialsContainer}>
        {testimonials.map((testimonial, index) => (
          <div key={index} className={styles.testimonialCard}>
            {testimonial.img && (
              <img
                src={testimonial.img}
                alt={testimonial.name}
                className={styles.testimonialImage}
              />
            )}
            <h4 className={styles.testimonialName}>{testimonial.name}</h4>
            <span className={styles.testimonialTitle}>{testimonial.title}</span>
            <p className={styles.testimonialText}>{testimonial.text}</p>
            <div className={styles.testimonialRating}>⭐⭐⭐⭐⭐</div>
          </div>
        ))}
      </div>

      <div className={styles.paginationDots}>
        {Array.from({
          length: Math.ceil(testimonials.length / itemsPerPage),
        }).map((_, index) => (
          <button
            key={index}
            className={`${styles.dot} ${
              currentPage === index + 1 ? styles.activeDot : ""
            }`}
            onClick={() => scrollToPage(index + 1)}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default TestimonialSection;